/**
 * SAM3D Inference Plugin
 *
 * Image -> 3D mesh (.glb) via SAM3 segmentation + SAM3D reconstruction.
 */

import type { SceneObject, TraceScene } from 'trace-viewer/scene';
import { INFERENCE_API_URL, getImageBlob, imageBlobExtension, createObjectFromInference, inferencePost, type InferenceStatusCallback } from './shared';
import { declareInferencePlugin } from '../plugins/inference-adapter';

async function run(sourceObject: SceneObject, traceScene: TraceScene, onStatus?: InferenceStatusCallback): Promise<SceneObject> {
    const imageBlob = await getImageBlob(sourceObject, traceScene);
    const imgExt = imageBlobExtension(imageBlob);

    // The /pipeline/image-to-3d endpoint orchestrates sam3 + sam3d; probe
    // sam3d (the final stage) for readiness — the gateway has no /pipeline/health.
    const response = await inferencePost('sam3d', () => {
        const formData = new FormData();
        formData.append('image', imageBlob, `image.${imgExt}`);
        formData.append('format', 'glb');
        return [`${INFERENCE_API_URL}/pipeline/image-to-3d`, { method: 'POST', body: formData }];
    }, undefined, onStatus);
    if (!response.ok) {
        const errText = await response.text();
        throw new Error(`image-to-3d pipeline error (${response.status}): ${errText}`);
    }

    const glbBlob = await response.blob();
    const assetPath = await traceScene.resources.addAssetWithDedup(
        `assets/${sourceObject.name}.glb`,
        glbBlob,
    );

    return createObjectFromInference(traceScene, sourceObject, assetPath, '3d');
}

declareInferencePlugin({
    id: 'sam3d',
    sourceType: 'image',
    menuLabel: 'Make 3D (SAM3D)',
    menuOrder: 205,

    toolName: 'generate_3d_sam3d',

    run,

    slashCommand: {
        name: 'sam3d',
        description: 'Convert image to 3D mesh (SAM3D)',
        usage: '[object]',
        category: 'inference',
        parseArgs: (args, selected) => ({
            object: args || selected || 'selected',
        }),
    },
});
